import { useState, useEffect } from "react";
import { apiClient } from "../../api/apiServices";

export default function CouponPicker({ service, amount, onApply, onRemove, appliedCoupon }) {
  const [available, setAvailable]   = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [code,    setCode]    = useState("");
  const [checking, setChecking] = useState(false);
  const [error,   setError]   = useState("");
  const [open,    setOpen]    = useState(false);

  useEffect(() => {
    if (!amount || amount < 1) { setAvailable([]); return; }
    setLoadingList(true);
    apiClient.get(`/mtrade/coupons/available?service=${service}&amount=${amount}`)
      .then(r => { if (r.data.status) setAvailable(r.data.data); })
      .catch(() => {})
      .finally(() => setLoadingList(false));
  }, [service, amount]);

  const handleManualValidate = async () => {
    if (!code.trim()) return;
    setChecking(true);
    setError("");
    try {
      const r = await apiClient.post("/mtrade/coupons/validate", { code: code.trim(), service, amount });
      if (r.data.status) {
        onApply({ couponId: r.data.couponId, code: r.data.code, discount: r.data.discount });
        setOpen(false);
        setCode("");
      } else {
        setError(r.data.message || "Invalid coupon");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Could not validate coupon");
    } finally { setChecking(false); }
  };

  const handleSelectAvailable = (c) => {
    onApply({ couponId: c._id, code: c.code, discount: c.discount });
    setOpen(false);
  };

  if (appliedCoupon) {
    return (
      <div className="flex items-center justify-between px-4 py-3 bg-success-light border border-success-border rounded-2xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "var(--color-success-border)" }}>
            <i className="ri-coupon-3-line text-sm" style={{ color: "var(--color-success-text)" }} />
          </div>
          <div>
            <p className="text-xs font-bold" style={{ color: "var(--color-success-text)" }}>{appliedCoupon.code}</p>
            <p className="text-xs" style={{ color: "var(--color-success)" }}>₦{appliedCoupon.discount.toLocaleString()} off</p>
          </div>
        </div>
        <button onClick={onRemove} className="text-xs text-danger hover:opacity-80 font-semibold px-3 py-1.5 rounded-xl transition-colors"
          style={{ background: "transparent" }}>
          Remove
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-2xl border-2 transition-all"
        style={{
          borderStyle: open ? "solid" : "dashed",
          borderColor: open ? "var(--color-brand)" : "var(--color-border-strong)",
          background:  open ? "var(--color-brand-light)" : "var(--color-surface)",
        }}
      >
        <div className="flex items-center gap-2">
          <i className="ri-coupon-3-line text-muted text-base" />
          <span className="text-secondary text-sm">
            {available.length > 0 ? `${available.length} coupon${available.length > 1 ? "s" : ""} available` : "Apply coupon code"}
          </span>
        </div>
        {available.length > 0 && !open && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-success-light" style={{ color: "var(--color-success-text)" }}>
            {available.length} available
          </span>
        )}
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line text-muted text-sm`} />
      </button>

      {open && (
        <div className="mt-2 bg-surface border border-border rounded-2xl overflow-hidden shadow-lg">
          {loadingList ? (
            <div className="px-4 py-4 text-center">
              <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin mx-auto"
                style={{ borderColor: "var(--color-brand)", borderTopColor: "transparent" }} />
            </div>
          ) : available.length > 0 ? (
            <>
              <div className="px-4 py-2.5 bg-surface-muted border-b border-border">
                <p className="text-muted text-[10px] font-bold uppercase tracking-wide">Your Coupons</p>
              </div>
              {available.map(c => (
                <button key={c._id} onClick={() => handleSelectAvailable(c)}
                  className="w-full flex items-center justify-between px-4 py-3 border-b border-border hover:bg-surface-hover transition-colors text-left">
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-bold text-primary font-mono">{c.code}</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-success-light uppercase" style={{ color: "var(--color-success-text)" }}>
                        {c.type === "percent" ? `${c.value}% off` : `₦${c.value} off`}
                      </span>
                    </div>
                    {c.description && <p className="text-muted text-xs">{c.description}</p>}
                    {c.expiresAt && (
                      <p className="text-faint text-[10px] mt-0.5">
                        Expires {new Date(c.expiresAt).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <p className="font-bold text-sm" style={{ color: "var(--color-success)" }}>-₦{c.discount?.toLocaleString()}</p>
                    <p className="text-faint text-[10px]">tap to apply</p>
                  </div>
                </button>
              ))}
            </>
          ) : null}

          <div className="px-4 py-3">
            {available.length > 0 && (
              <p className="text-faint text-[10px] font-semibold uppercase tracking-wide mb-2">Or enter a code</p>
            )}
            {error && (
              <div className="flex items-center gap-1.5 px-3 py-2 bg-danger-light border border-danger-border rounded-xl mb-2 text-xs"
                style={{ color: "var(--color-danger-text)" }}>
                <i className="ri-error-warning-line" />{error}
              </div>
            )}
            <div className="flex gap-2">
              <input
                value={code}
                onChange={e => { setCode(e.target.value.toUpperCase()); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleManualValidate()}
                placeholder="ENTER CODE"
                className="flex-1 bg-surface-muted border border-border-strong rounded-xl px-3 py-2.5 text-sm text-primary font-mono uppercase placeholder:text-faint outline-none focus:border-brand tracking-widest"
              />
              <button onClick={handleManualValidate} disabled={checking || !code.trim()}
                className="px-4 py-2.5 bg-brand hover:bg-brand-hover disabled:opacity-40 text-on-brand text-sm font-bold rounded-xl transition-colors">
                {checking ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : "Apply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}